import { useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import {
  isAal2Required,
  uploadListingPhoto,
  useListing,
  usePublishListing,
  useUpdateListing,
} from '@/lib/data/listings';
import { EdgeFunctionError } from '@/lib/api/edgeFunction';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/listings/$id/edit')({
  component: ListingEditPage,
});

function ListingEditPage() {
  const { id } = useParams({ from: '/listings/$id/edit' });
  const { supabase, session } = useAuth();
  const navigate = useNavigate();
  const listingQuery = useListing(supabase, id);
  const updateListing = useUpdateListing(supabase);
  const publishListing = usePublishListing(supabase);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priceUnit, setPriceUnit] = useState('per night');
  const [maxGuests, setMaxGuests] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [initialised, setInitialised] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [publishMessages, setPublishMessages] = useState<string[] | null>(null);
  const [publishOk, setPublishOk] = useState(false);

  const listing = listingQuery.data;
  if (listing && !initialised) {
    setTitle(listing.title);
    setDescription(listing.description ?? '');
    setPrice(listing.price != null ? String(listing.price) : '');
    setPriceUnit(listing.price_unit ?? 'per night');
    setMaxGuests(listing.max_guests != null ? String(listing.max_guests) : '');
    setImages(listing.images ?? []);
    setInitialised(true);
  }

  function handleAal2(err: unknown): boolean {
    if (isAal2Required(err)) {
      navigate({ to: '/sign-in/mfa', search: { next: `/listings/${id}/edit` } });
      return true;
    }
    return false;
  }

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0 || !listing || !session) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded: string[] = [];
      let index = images.length;
      for (const file of Array.from(files)) {
        const url = await uploadListingPhoto(supabase, file, session.user.id, listing.business_id, index);
        uploaded.push(url);
        index += 1;
      }
      const nextImages = [...images, ...uploaded];
      setImages(nextImages);
      await updateListing.mutateAsync({ id: listing.id, images: nextImages });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Photo upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function removePhoto(url: string) {
    if (!listing) return;
    const nextImages = images.filter((img) => img !== url);
    setImages(nextImages);
    updateListing.mutate({ id: listing.id, images: nextImages });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!listing) return;
    setError(null);
    setSaved(false);
    updateListing.mutate(
      {
        id: listing.id,
        title,
        description: description || null,
        price: price ? Number(price) : null,
        price_unit: priceUnit || null,
        max_guests: maxGuests ? Number(maxGuests) : null,
      },
      {
        onSuccess: () => setSaved(true),
        onError: (err) => setError(err instanceof Error ? err.message : 'Could not save.'),
      },
    );
  }

  function handlePublish() {
    if (!listing) return;
    setPublishMessages(null);
    setPublishOk(false);
    publishListing.mutate(listing.id, {
      onSuccess: () => setPublishOk(true),
      onError: (err) => {
        if (handleAal2(err)) return;
        if (err instanceof EdgeFunctionError && err.code === 'publish_blocked') {
          const messages = Array.isArray(err.body.messages) ? (err.body.messages as string[]) : [];
          setPublishMessages(messages.length > 0 ? messages : ['This listing is not ready to publish yet.']);
          return;
        }
        setPublishMessages([err instanceof Error ? err.message : 'Could not publish this listing.']);
      },
    });
  }

  if (listingQuery.isLoading) return <PageLoading />;
  if (listingQuery.isError || !listing) return <p className="text-body text-danger">Could not load this listing.</p>;

  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-lg">
      <div className="flex items-center justify-between">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Edit listing</h1>
        <Link to="/listings" className="text-body text-canyon underline">
          All listings
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-md rounded-card border border-border bg-surface p-lg shadow-card">
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
          />
        </label>
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
          />
        </label>
        <div className="flex flex-wrap gap-md">
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Price (N$)
            <input
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Price unit
            <input
              value={priceUnit}
              onChange={(e) => setPriceUnit(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Max guests
            <input
              type="number"
              min={1}
              value={maxGuests}
              onChange={(e) => setMaxGuests(e.target.value)}
              className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            />
          </label>
        </div>

        {error ? <p className="text-body text-danger">{error}</p> : null}
        {saved ? <p className="text-body text-success">Saved.</p> : null}
        <button
          type="submit"
          disabled={updateListing.isPending}
          className="self-start rounded-button bg-canyon px-lg py-sm text-body font-bold text-white disabled:opacity-50"
        >
          {updateListing.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Photos</h2>
        <p className="mt-xs text-body text-ink-2">
          Photos are re-encoded on upload to remove location data before they're stored.
        </p>
        {images.length > 0 ? (
          <div className="mt-md grid grid-cols-3 gap-sm sm:grid-cols-4">
            {images.map((url) => (
              <div key={url} className="relative">
                <img src={url} alt="" className="aspect-square w-full rounded-input object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(url)}
                  className="absolute right-xs top-xs rounded-full bg-ink/70 px-xs text-caption text-white"
                  aria-label="Remove photo"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotoChange}
          disabled={uploading}
          aria-label="Add photos"
          className="mt-md text-body text-ink"
        />
        {uploading ? <p className="mt-sm text-body text-ink-2">Uploading…</p> : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Publish</h2>
        <p className="mt-xs text-body text-ink-2">
          Status: <strong className="text-ink">{listing.is_active ? 'Published' : 'Draft'}</strong>. Publishing needs
          an authenticator step-up.
        </p>
        <button
          type="button"
          onClick={handlePublish}
          disabled={publishListing.isPending || listing.is_active}
          className="mt-md rounded-button bg-canyon px-lg py-sm text-body font-bold text-white disabled:opacity-50"
        >
          {publishListing.isPending ? 'Publishing…' : listing.is_active ? 'Already published' : 'Publish'}
        </button>
        {publishOk ? <p className="mt-sm text-body text-success">Published.</p> : null}
        {publishMessages ? (
          <ul className="mt-sm list-disc pl-lg text-body text-danger">
            {publishMessages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <Link to="/listings/$id/ical" params={{ id: listing.id }} className="text-body text-canyon underline">
        Calendar sync (iCal export/import) →
      </Link>
    </section>
  );
}
